import { useState } from 'react'
import { useOnboarding } from '../../../contexts/OnboardingContext'
import { Building2, MapPin, Shield, Check, Loader } from 'lucide-react'

export function ReviewStep() {
  const { data, nextStep, previousStep } = useOnboarding()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')
    
    try {
      // Create company
      const companyResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/companies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          name: data.companyName,
          industry: data.industry,
          timezone: data.timezone
        })
      })
      
      if (!companyResponse.ok) {
        throw new Error('Failed to create company')
      }
      
      const companyResult = await companyResponse.json()
      const company = companyResult.company
      
      // Create default team within the company
      const teamResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/teams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          name: 'Main Team',
          company_id: company.id,
          tier: 1
        })
      })
      
      if (!teamResponse.ok) {
        throw new Error('Failed to create default team')
      }
      
      const teamResult = await teamResponse.json()
      const team = teamResult.team
      
      // Create location
      const locationResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          team_id: team.id,
          name: data.locationName,
          type: data.locationType,
          address: data.address,
          geofence: data.geofence
        })
      })
      
      if (!locationResponse.ok) {
        throw new Error('Failed to create location')
      }
      
      const locationResult = await locationResponse.json()
      const location = locationResult.location
      
      // Create constraint set if any constraints are enabled
      if (data.requireGeo || data.requireQR || data.requirePhoto || data.timeWindows?.length) {
        const constraintResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/constraint-sets`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            team_id: team.id,
            name: 'Default Constraints',
            applies_to: 'BOTH',
            require_qr: data.requireQR,
            require_photo: data.requirePhoto,
            require_geo: data.requireGeo,
            require_wifi: false, // Disabled until native app
            time_windows: data.timeWindows,
            location_ids: [location.id]
          })
        })
        
        if (!constraintResponse.ok) {
          throw new Error('Failed to create constraints')
        }
      }
      
      nextStep()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setIsSubmitting(false)
    }
  }
  
  const getVerificationMethods = () => {
    const methods = []
    if (data.requireGeo) methods.push('Location verification')
    if (data.requireQR) methods.push('QR code scanning')
    if (data.requirePhoto) methods.push('Photo verification')
    // WiFi verification disabled until native app
    // if (data.requireWifi) methods.push('WiFi network verification')
    return methods.length > 0 ? methods : ['No verification required']
  }
  
  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Review your setup
        </h2>
        <p className="text-gray-600">
          Please review your company details before we create your account
        </p>
      </div>
      
      <div className="space-y-6">
        {/* Company details */}
        <div className="p-6 bg-gray-50 rounded-lg">
          <div className="flex items-center mb-4">
            <Building2 className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Company</h3>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Name:</span>
              <p className="font-medium">{data.companyName}</p>
            </div>
            <div>
              <span className="text-gray-600">Industry:</span>
              <p className="font-medium">{data.industry || 'Not specified'}</p>
            </div>
            <div className="col-span-2">
              <span className="text-gray-600">Timezone:</span>
              <p className="font-medium">{data.timezone}</p>
            </div>
          </div>
        </div>
        
        {/* Location details */}
        <div className="p-6 bg-gray-50 rounded-lg">
          <div className="flex items-center mb-4">
            <MapPin className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">First Location</h3>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Name:</span>
              <p className="font-medium">{data.locationName}</p>
            </div>
            <div>
              <span className="text-gray-600">Type:</span>
              <p className="font-medium capitalize">{data.locationType}</p>
            </div>
            {data.locationType === 'physical' && data.address && (
              <div className="col-span-2">
                <span className="text-gray-600">Address:</span>
                <p className="font-medium">{data.address}</p>
              </div>
            )}
            {data.geofence && (
              <div className="col-span-2">
                <span className="text-gray-600">Geofencing:</span>
                <p className="font-medium">Enabled</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Constraints */}
        <div className="p-6 bg-gray-50 rounded-lg">
          <div className="flex items-center mb-4">
            <Shield className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Clock-In Requirements</h3>
          </div>
          <div className="text-sm">
            <span className="text-gray-600">Verification methods:</span>
            <ul className="mt-2 space-y-1">
              {getVerificationMethods().map((method, index) => (
                <li key={index} className="flex items-center">
                  <Check className="w-4 h-4 text-green-600 mr-2" />
                  <span className="font-medium">{method}</span>
                </li>
              ))}
            </ul>
            {data.timeWindows?.length && (
              <div className="mt-3">
                <span className="text-gray-600">Time restrictions:</span>
                <p className="font-medium">
                  {data.timeWindows[0].startTime} - {data.timeWindows[0].endTime}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
      
      {/* Navigation */}
      <div className="mt-8 flex justify-between">
        <button
          type="button"
          onClick={previousStep}
          disabled={isSubmitting}
          className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium disabled:opacity-50"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 flex items-center"
        >
          {isSubmitting ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Creating...
            </>
          ) : (
            'Create Company'
          )}
        </button>
      </div>
    </form>
  )
}

export default ReviewStep